import { useEffect, useReducer, useState } from 'react'
import { keepPreviousData, useMutation, useMutationState, useQuery, useQueryClient } from '@tanstack/react-query'
import { notification } from 'antd'
import { t } from 'i18next'
import {
    analyzeVoice,
    AnswerInput,
    updateVoice,
    checkInstallation,
    createScreening,
    createSite,
    CrawlInput,
    fetchChatSettings,
    fetchCrawlSettings,
    fetchFormStyle,
    suggestQuestions,
    updateChatSettings,
    updateCrawlSettings,
    updateFormStyle,
    applyStartMessage,
    AssistantInput,
    CreateSiteInput,
    SuggestQuestionsInput,
    VacancyFilters,
    authorizeSite,
    fetchSiteLimit,
    deleteAnswer,
    fetchAnswers,
    fetchUnanswered,
    saveAnswer,
    ChatFilters,
    disconnectSite,
    DocumentFilters,
    fetchAssistant,
    fetchDocument,
    fetchSite,
    fetchSiteChat,
    fetchSiteChats,
    fetchSiteDocuments,
    fetchSiteOverview,
    fetchSites,
    fetchSiteVacancies,
    fetchWhatsapp,
    resyncSite,
    tryOutAssistant,
    TryOutInput,
    updateAssistant,
    updateSite,
    updateWhatsapp,
    WhatsappInput,
} from './api'
import { ChatSettings, ConnectRequest, ScreeningQuestion, SiteOverview, VoiceInput } from './site'

export function useSites(enabled = true) {
    return useQuery({
        queryFn: () => fetchSites(),
        queryKey: ['sites'],
        enabled,
    })
}

export function useSite(siteId: string) {
    return useQuery({
        queryFn: () => fetchSite(siteId),
        queryKey: ['sites', siteId],
    })
}

/** After "Read again", how long to keep checking for the reading to start: a crawl waits in the queue first. */
const RESYNC_WINDOW = 180_000

/** Stops checking when the reading made no progress for this long, for example when a page is stuck. */
const STALLED_AFTER = 120_000

type ReadingProgress = {
    signature: string
    since: number
}

const resyncedAt = new Map<string, number>()
const progress = new Map<string, ReadingProgress>()

/** Whether Evaa is reading the website: crawling, or pages waiting to be read or added to its knowledge. */
export function isReading(overview?: SiteOverview): boolean {
    if (!overview) {
        return false
    }

    const { fetched, pending, indexed } = overview.content

    return Boolean(overview.connection.crawling) || pending > 0 || indexed < fetched
}

/** Whether to keep checking how far the reading got, so the progress shows without refreshing. */
function followsReading(siteId: string, overview?: SiteOverview): boolean {
    const now = Date.now()
    const isJustResynced = now - (resyncedAt.get(siteId) ?? 0) < RESYNC_WINDOW

    if (isJustResynced) {
        return true
    }

    if (!overview || !isReading(overview)) {
        progress.delete(siteId)

        return false
    }

    const { fetched, pending, indexed } = overview.content
    const signature = `${overview.connection.crawling}/${fetched}/${pending}/${indexed}`
    const last = progress.get(siteId)

    if (last?.signature !== signature) {
        progress.set(siteId, {
            signature,
            since: now,
        })

        return true
    }

    return now - last.since < STALLED_AFTER
}

export function useSiteOverview(siteId: string) {
    return useQuery({
        queryFn: () => fetchSiteOverview(siteId),
        queryKey: ['sites', siteId, 'overview'],
        refetchInterval: (query) => (followsReading(siteId, query.state.data) ? 3000 : false),
    })
}

/** Whether the pages are being read, while the progress is followed: lists of pages then refresh too. */
export function useFollowsReading(siteId: string): boolean {
    const { data } = useSiteOverview(siteId)

    return isReading(data) && followsReading(siteId, data)
}

/** Whether "Read again" was just pressed and the reading has not shown yet: the crawl may still wait in the queue. */
export function useStartsReading(siteId: string): boolean {
    const { data } = useSiteOverview(siteId)

    const pressedAt = useMutationState({
        filters: {
            mutationKey: ['sites', siteId, 'resync'],
            status: 'success',
        },
        select: (mutation) => mutation.state.submittedAt,
    }).reduce((latest, at) => Math.max(latest, at), 0)

    const [, rerender] = useReducer((renders: number) => renders + 1, 0)

    // The press after which the reading showed: from then on, the reading itself tells whether Evaa is busy.
    const [shownAfter, setShownAfter] = useState(0)
    const shown = shownAfter === pressedAt || (pressedAt > 0 && isReading(data))

    if (shown && shownAfter !== pressedAt) {
        setShownAfter(pressedAt)
    }

    // Once more when the time is up, as nothing else may change by then.
    useEffect(() => {
        const left = pressedAt + RESYNC_WINDOW - Date.now()

        if (left > 0) {
            const timer = setTimeout(rerender, left)

            return () => clearTimeout(timer)
        }
    }, [pressedAt])

    return !shown && Date.now() - pressedAt < RESYNC_WINDOW
}

export function useSiteVacancies(siteId: string, filters: VacancyFilters = {}) {
    return useQuery({
        queryFn: () => fetchSiteVacancies(siteId, filters),
        queryKey: ['sites', siteId, 'vacancies', filters],
        placeholderData: keepPreviousData,
    })
}

export function useSiteChats(siteId: string, filters: ChatFilters = {}) {
    return useQuery({
        queryFn: () => fetchSiteChats(siteId, filters),
        queryKey: ['sites', siteId, 'chats', filters],
        placeholderData: keepPreviousData,
    })
}

export function useSiteChat(siteId: string, chatId: string | null) {
    return useQuery({
        queryFn: () => fetchSiteChat(siteId, chatId!),
        queryKey: ['sites', siteId, 'chats', 'detail', chatId],
        enabled: chatId !== null,
    })
}

type SiteDocumentsPage = Awaited<ReturnType<typeof fetchSiteDocuments>>

type SiteDocumentsOptions = {
    filters?: DocumentFilters
    perPage?: number
    enabled?: boolean
    refetchInterval?: number | false | ((data?: SiteDocumentsPage) => number | false)
}

export function useSiteDocuments(
    siteId: string,
    { filters = {}, perPage = 15, enabled = true, refetchInterval = false }: SiteDocumentsOptions = {}
) {
    return useQuery({
        queryFn: () => fetchSiteDocuments(siteId, filters, perPage),
        queryKey: ['sites', siteId, 'documents', filters, perPage],
        placeholderData: keepPreviousData,
        enabled,
        refetchInterval: (query) =>
            typeof refetchInterval === 'function' ? refetchInterval(query.state.data) : refetchInterval,
    })
}

export function useFetchDocument(siteId: string) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (documentId: string) => fetchDocument(siteId, documentId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['sites', siteId, 'documents'] })
            notification.success({ message: t('The page will be read again in a moment') })
        },
    })
}

export function useResyncSite(siteId: string) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationKey: ['sites', siteId, 'resync'],
        mutationFn: () => resyncSite(siteId),
        onSuccess: ({ queued }) => {
            resyncedAt.set(siteId, Date.now())
            queryClient.invalidateQueries({ queryKey: ['sites', siteId] })
            notification.success({ message: t('{{count}} pages will be read again', { count: queued }) })
        },
    })
}

export function useAssistant(siteId: string, refetchInterval?: number | false) {
    return useQuery({
        queryFn: () => fetchAssistant(siteId),
        queryKey: ['sites', siteId, 'assistant'],
        refetchInterval,
    })
}

export function useAnalyzeVoice(siteId: string) {
    return useMutation({ mutationFn: () => analyzeVoice(siteId) })
}

export function useTryOutAssistant(siteId: string) {
    return useMutation({ mutationFn: (input: TryOutInput) => tryOutAssistant(siteId, input) })
}

export function useWhatsapp(siteId: string) {
    return useQuery({
        queryFn: () => fetchWhatsapp(siteId),
        queryKey: ['sites', siteId, 'whatsapp'],
    })
}

export function useUpdateWhatsapp(siteId: string) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (input: WhatsappInput) => updateWhatsapp(siteId, input),
        onSuccess: (data) => {
            queryClient.setQueryData(['sites', siteId, 'whatsapp'], data)
            queryClient.invalidateQueries({ queryKey: ['sites', siteId, 'overview'] })
            notification.success({ message: t('WhatsApp settings saved') })
        },
    })
}

export function useUpdateSite(siteId: string) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (input: { candidatesInWordpress: boolean }) => updateSite(siteId, input),
        onSuccess: (data) => {
            queryClient.setQueryData(['sites', siteId], data)
            notification.success({ message: t('Settings saved') })
        },
    })
}

export function useAuthorizeSite() {
    return useMutation({
        mutationFn: (input: ConnectRequest) => authorizeSite(input),
        onSuccess: ({ redirectUrl }) => {
            window.location.href = redirectUrl
        },
    })
}

export function useDisconnectSite() {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (siteId: string) => disconnectSite(siteId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['sites'] })
            notification.success({ message: t('Website disconnected') })
        },
    })
}

export function useUnanswered(siteId: string, days: number, search?: string) {
    return useQuery({
        queryFn: () => fetchUnanswered(siteId, days, search),
        queryKey: ['sites', siteId, 'unanswered', days, search],
        placeholderData: keepPreviousData,
    })
}

export function useAnswers(siteId: string) {
    return useQuery({
        queryFn: () => fetchAnswers(siteId),
        queryKey: ['sites', siteId, 'answers'],
    })
}

type SaveAnswerVariables = {
    input: AnswerInput
    answerId?: string
}

export function useSaveAnswer(siteId: string) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: ({ input, answerId }: SaveAnswerVariables) => saveAnswer(siteId, input, answerId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['sites', siteId, 'answers'] })
            queryClient.invalidateQueries({ queryKey: ['sites', siteId, 'unanswered'] })
            queryClient.invalidateQueries({ queryKey: ['sites', siteId, 'overview'] })
            notification.success({ message: t('Answer saved. Evaa uses it from now on.') })
        },
    })
}

export function useDeleteAnswer(siteId: string) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (answerId: string) => deleteAnswer(siteId, answerId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['sites', siteId, 'answers'] })
            queryClient.invalidateQueries({ queryKey: ['sites', siteId, 'unanswered'] })
        },
    })
}

export function useApplyStartMessage(siteId: string) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: () => applyStartMessage(siteId),
        onSuccess: ({ applied }) => {
            queryClient.invalidateQueries({ queryKey: ['sites', siteId, 'whatsapp'] })
            notification.success({ message: t('Start message used for {{count}} vacancies', { count: applied }) })
        },
    })
}

export function useCreateSite() {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (input: CreateSiteInput) => createSite(input),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['sites'] }),
    })
}

export function useSiteLimit(enabled = true) {
    return useQuery({
        queryKey: ['sites', 'limit'],
        queryFn: () => fetchSiteLimit(),
        enabled,
    })
}

export function useCheckInstallation(siteId: string) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: () => checkInstallation(siteId),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['sites', siteId] }),
    })
}

export function useChatSettings(siteId: string) {
    return useQuery({
        queryFn: () => fetchChatSettings(siteId),
        queryKey: ['sites', siteId, 'chat-settings'],
    })
}

export function useUpdateChatSettings(siteId: string) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (input: Partial<ChatSettings>) => updateChatSettings(siteId, input),
        onSuccess: (data) => {
            queryClient.setQueryData(['sites', siteId, 'chat-settings'], data)
            queryClient.invalidateQueries({ queryKey: ['sites', siteId, 'overview'] })
            queryClient.invalidateQueries({
                queryKey: ['sites', siteId],
                exact: true,
            })
            notification.success({ message: t('Settings saved') })
        },
    })
}

export function useCrawlSettings(siteId: string, enabled = true) {
    return useQuery({
        queryFn: () => fetchCrawlSettings(siteId),
        queryKey: ['sites', siteId, 'crawl'],
        enabled,
        // Shows when a crawl that is running has finished.
        refetchInterval: (query) => (query.state.data?.crawling ? 10000 : false),
    })
}

/**
 * Only the rules of the crawl settings: the same object while the rules do not change, so the pages table does not
 * render again each time the status is polled while Evaa reads the website.
 */
export function useCrawlRuleSettings(siteId: string) {
    return useQuery({
        queryFn: () => fetchCrawlSettings(siteId),
        queryKey: ['sites', siteId, 'crawl'],
        select: ({ sitemapUrl, exclude, vacancyPaths, notVacancyPaths }) => ({
            sitemapUrl,
            exclude,
            vacancyPaths,
            notVacancyPaths,
        }),
    })
}

export function useUpdateCrawlSettings(siteId: string) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (input: CrawlInput) => updateCrawlSettings(siteId, input),
        // The caller says what happens next: only the pages a rule changes, or the whole website, are read again.
        onSuccess: (data) => {
            queryClient.setQueryData(['sites', siteId, 'crawl'], data)
            // Pages, vacancies and their count change with the rules.
            queryClient.invalidateQueries({ queryKey: ['sites', siteId] })
        },
    })
}

export function useSuggestQuestions(siteId: string, vacancyId: string) {
    return useMutation({
        mutationFn: (input: SuggestQuestionsInput) => suggestQuestions(siteId, vacancyId, input),
    })
}

export function useCreateScreening(siteId: string, vacancyId: string) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (questions: ScreeningQuestion[]) => createScreening(siteId, vacancyId, questions),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['sites', siteId] })
        },
    })
}

export function useFormStyle(siteId: string) {
    return useQuery({
        queryFn: () => fetchFormStyle(siteId),
        queryKey: ['sites', siteId, 'form-style'],
    })
}

type UpdateFormStyleVariables = {
    style: Record<string, unknown>
    apply: boolean
}

export function useUpdateFormStyle(siteId: string) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: ({ style, apply }: UpdateFormStyleVariables) => updateFormStyle(siteId, style, apply),
        onSuccess: (data) => {
            queryClient.setQueryData(['sites', siteId, 'form-style'], data)

            const message = data.applied
                ? t('Style saved and used for {{count}} forms', { count: data.applied })
                : t('Style saved')

            notification.success({ message })
        },
    })
}

type AssistantPageInput = {
    assistant?: AssistantInput
    chat?: Partial<ChatSettings>
    voice?: VoiceInput
}

/**
 * Saves the assistant and the chat settings together, from the Assistant page, with one notification.
 */
export function useSaveAssistantPage(siteId: string) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async ({ assistant, chat, voice }: AssistantPageInput) => {
            const [savedAssistant, savedChat] = await Promise.all([
                assistant ? updateAssistant(siteId, assistant) : undefined,
                chat ? updateChatSettings(siteId, chat) : undefined,
            ])

            // After the assistant, so its response has both.
            const savedVoice = voice ? await updateVoice(siteId, voice) : undefined

            return {
                assistant: savedVoice ?? savedAssistant,
                chat: savedChat,
            }
        },
        onSuccess: ({ assistant, chat }) => {
            if (assistant) {
                queryClient.setQueryData(['sites', siteId, 'assistant'], assistant)
            }

            if (chat) {
                queryClient.setQueryData(['sites', siteId, 'chat-settings'], chat)
                queryClient.invalidateQueries({ queryKey: ['sites', siteId, 'overview'] })
                queryClient.invalidateQueries({
                    queryKey: ['sites', siteId],
                    exact: true,
                })
            }

            notification.success({ message: t('Settings saved') })
        },
    })
}
